import { Segmented } from 'antd';
import { useField } from 'formik';
import { get, keys, omit, pick, set, uniq } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactJson, { type ReactJsonViewProps } from 'react-json-view';
import mergeTranslations from '../../../../server/utils/mergeTranslations';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import useSystemTranslations from './useSystemTranslations';

type Mode = 'self' | 'computed' | 'system';

const commonJsonProps: Partial<ReactJsonViewProps> = {
    displayDataTypes: false,
    displayObjectSize: false,
    enableClipboard: false,
    name: null,
    quotesOnKeys: false,
    sortKeys: true,
    shouldCollapse: field => field.name !== null,
};

const LanguagePackTranslations = () => {
    const { t } = useTranslation(['languagePackDetails']);
    const [{ value }, , { setValue }] = useField('translations');

    const [mode, setMode] = useState<Mode>('self');

    // get initial translations
    const { initialTranslations, loading } = useSystemTranslations();

    const actions = useMemo(() => {
        const getPathToUpdate = (path: string[]) => {
            const relativePath = [];

            for (const part of path) {
                relativePath.push(part);
                const relativeValue = get(relativePath, initialTranslations);

                if (Array.isArray(relativeValue)) {
                    return relativePath;
                }
            }

            return path;
        };

        const onEdit: ReactJsonViewProps['onEdit'] = event => {
            const path = getPathToUpdate([...event.namespace, event.name]);
            setValue(set(path, get(path, event.updated_src), value));
        };

        const onDelete: ReactJsonViewProps['onEdit'] = event => {
            setValue(omit([...event.namespace, event.name], value));
        };

        const onAdd: ReactJsonViewProps['onAdd'] = event => {
            if (event.name) {
                const path = getPathToUpdate([...event.namespace, event.name]);
                setValue(set(path, get(path, event.updated_src), value));
            } else {
                const validNamespaces = uniq([...keys(initialTranslations), ...keys(value)]);
                setValue(pick(validNamespaces, event.new_value));
            }
        };

        return { onEdit, onDelete, onAdd };
    }, [value, setValue, initialTranslations]);

    const { mergedTranslations, cleanedValue } = useMemo(
        () => ({
            mergedTranslations: mergeTranslations(initialTranslations, value),
            cleanedValue: pick(keys(initialTranslations), value),
        }),
        [initialTranslations, value]
    );

    const renderContent = () => {
        switch (mode) {
            case 'system':
                return <ReactJson {...commonJsonProps} src={initialTranslations} enableClipboard />;

            case 'computed':
                return <ReactJson {...commonJsonProps} {...actions} src={mergedTranslations} />;

            case 'self':
                return <ReactJson {...commonJsonProps} {...actions} src={cleanedValue} />;

            default:
                throw new Error('Mode not supported');
        }
    };

    const modes = (
        <Segmented
            onChange={value => setMode(value as unknown as Mode)}
            options={[
                { value: 'self', label: t('languagePackDetails:options.mode.self') },
                { value: 'computed', label: t('languagePackDetails:options.mode.computed') },
                { value: 'system', label: t('languagePackDetails:options.mode.system') },
            ]}
            value={mode}
        />
    );

    return (
        <CollapsibleWrapper defaultActiveKey={t('languagePackDetails:cardTitles.translations')}>
            <Panel
                key={t('languagePackDetails:cardTitles.translations')}
                extra={modes}
                header={t('languagePackDetails:cardTitles.translations')}
            >
                {!loading && renderContent()}
            </Panel>
        </CollapsibleWrapper>
    );
};

export default LanguagePackTranslations;
